import { sections } from '../About/helpers';
import { isOntologyGraphFeatureVisible } from '@/config/ontologyGraph';
import { isGeoBrowseEnabled } from '@/config/geoBrowse';
import { isGseaEnabled } from '@/modules/enrichment/helpers';

export type NavMegaMenuId = 'docs' | 'tools';

export type DocsIconKey = 'book' | 'rocket' | 'chart' | 'database' | 'metrics' | 'document' | 'ontology';

export type DocsMenuItem = {
  label: string;
  subtitle: string;
  href: string;
  icon: DocsIconKey;
};

export type DocsMenuColumn = {
  title: string;
  items: DocsMenuItem[];
};

export type NavToolMenuItem = {
  id: string;
  label: string;
  description: string;
  infoHref: string;
  toolHref: string;
  toolExternal?: boolean;
  showInfo?: boolean;
  showTool?: boolean;
  iconSrc?: string;
  iconWidth?: number;
  iconAlt?: string;
  roles?: string[];
  requiresOntologyGraph?: boolean;
  requiresGsea?: boolean;
  requiresGeoBrowse?: boolean;
};

export function toolShowsInfoLink(tool: NavToolMenuItem): boolean {
  if (tool.showInfo === false) return false;
  if (tool.showInfo === true) return true;
  return tool.infoHref !== tool.toolHref;
}

export function isNavToolVisible(tool: NavToolMenuItem, userRoles: string[]): boolean {
  if (tool.requiresOntologyGraph && !isOntologyGraphFeatureVisible) return false;
  if (tool.requiresGsea && !isGseaEnabled) return false;
  if (tool.requiresGeoBrowse && !isGeoBrowseEnabled) return false;
  if (!tool.roles) return true;
  return tool.roles.some(role => userRoles.includes(role));
}

const statisticsSections = sections.filter(s => s.group === 'Statistics');
const dataSections = sections.filter(s => s.group === 'Data');

const statisticsSubtitles: Record<string, string> = {
  gdaStatistics: 'Platform-wide metrics',
  stats_by_source: 'Breakdown per data source',
};

const dataSubtitles: Record<string, string> = {
  schema: 'Database structure',
  sources: 'Data origins',
  metrics: 'Scoring methodology',
  attributes: 'Field definitions',
  assoctypeont: 'Relationship types',
};

const dataIcons: Record<string, DocsIconKey> = {
  schema: 'database',
  sources: 'database',
  metrics: 'metrics',
  attributes: 'document',
  assoctypeont: 'ontology',
};

export const DOCS_MEGA_COLUMNS: DocsMenuColumn[] = [
  {
    title: 'DATA',
    items: dataSections.map(s => ({
      label: s.value === 'assoctypeont' ? 'Association Ontology' : s.label,
      subtitle: dataSubtitles[s.value] ?? 'Documentation',
      href: `/docs?section=${s.value}`,
      icon: dataIcons[s.value] ?? 'database',
    })),
  },
  {
    title: 'INFO',
    items: [
      {
        label: 'How to cite us',
        subtitle: 'Citation guidelines',
        href: '/docs?section=cite_us',
        icon: 'book',
      },
      {
        label: 'Releases',
        subtitle: 'Version history & changelog',
        href: '/docs?version=latest',
        icon: 'rocket',
      },
    ],
  },
  {
    title: 'STATISTICS',
    items: statisticsSections.map(s => ({
      label: s.label,
      subtitle: statisticsSubtitles[s.value] ?? 'Platform metrics',
      href: `/docs?section=${s.value}`,
      icon: 'chart' as DocsIconKey,
    })),
  },
];

export const DOCS_MEGA_MENU = {
  leftTitle: 'INFO',
  statisticsTitle: 'STATISTICS',
  statisticsItems: DOCS_MEGA_COLUMNS[1].items,
  dataTitle: 'DATA',
  dataItems: DOCS_MEGA_COLUMNS[2].items,
  rightItems: DOCS_MEGA_COLUMNS[0].items,
  overviewHref: '/docs',
};

export const DOCS_OVERVIEW_HREF = '/docs';

export const NAV_TOOLS_MENU: NavToolMenuItem[] = [
  {
    id: 'ai',
    label: 'AIDA',
    description: 'AI-driven discovery',
    infoHref: '/Tools?section=assistant',
    toolHref: '/Assistant',
    iconSrc: '/static/images/eyesAida.svg',
    iconAlt: 'AIDA',
  },
  {
    id: 'advanced-search',
    label: 'Advanced Search',
    description: 'Powerful query builder',
    infoHref: '/advancedSearch',
    toolHref: '/advancedSearch',
    showInfo: false,
  },
  {
    id: 'geo-browse',
    label: 'ExpressNET',
    description: 'Gene expression across diseases',
    infoHref: '/geo-browse',
    toolHref: '/geo-browse',
    showInfo: false,
    requiresGeoBrowse: true,
  },
  {
    id: 'enrichment',
    label: 'Enrichment',
    description: 'Functional analysis',
    infoHref: '/Tools?section=enrichment',
    toolHref: '/enrichment',
  },
  {
    id: 'gsea-ranked',
    label: 'GSEA',
    description: 'Ranked gene set enrichment analysis',
    infoHref: '/Tools?section=gsea',
    toolHref: '/gsea-ranked',
    requiresGsea: true,
  },
  {
    id: 'api',
    label: 'REST API',
    description: 'Programmatic access',
    infoHref: '/Tools?section=console',
    toolHref: '/interactive-console',
    iconSrc: '/static/images/icons/api-icon.svg',
    iconAlt: 'REST API',
  },
  {
    id: 'mcp',
    label: 'MCP Connector',
    description: 'Data Native to AI Agents',
    infoHref: '/Tools?section=mcp',
    toolHref: '/Tools?section=mcp',
    showInfo: true,
    showTool: false,
    iconSrc: '/static/images/icons/iconsPink/062.svg',
    iconWidth: 80,
    iconAlt: 'Cytoscape',
  },
  {
    id: 'r-package',
    label: 'R Package',
    description: 'R Integration',
    infoHref: '/Tools?section=Rinstallation',
    toolHref: 'https://gitlab.com/medbio/disgenet2r',
    toolExternal: true,
    iconSrc: '/static/images/icons/rPackage.svg',
    iconAlt: 'R Package',
  },
  {
    id: 'cytoscape',
    label: 'Cytoscape',
    description: 'Network visualization',
    infoHref: '/Tools?section=installcyto',
    toolHref: 'https://apps.cytoscape.org/apps/disgenetapp',
    toolExternal: true,
    iconSrc: '/static/images/icons/iconsPink/Cytoscape_logo.svg',
    iconAlt: 'Cytoscape',
  },
  {
    id: 'ontology-explorer',
    label: 'Ontology Explorer',
    description: 'Disease hierarchy',
    infoHref: '/ontology-graph',
    toolHref: '/ontology-graph',
    showInfo: false,
    requiresOntologyGraph: true,
  },
  {
    id: 'download',
    label: 'Download',
    description: 'Full database export',
    infoHref: '/Tools?section=download',
    toolHref: '/Tools?section=download',
    showInfo: false,
    roles: ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'],
  },
];

export const PRODUCT_DEMO_HREF = '/product-demo';
export const TOOLS_OVERVIEW_HREF = '/Tools';
